import type { ColumnSizingState } from '@tanstack/react-table';
import { parseDataTableColumnWidth } from './data-table/columnWidth.js';

const MIN_COLUMN_WIDTH_PX = 56;
/** Declarative `act` column (row actions in-column) — not a flex grow column. */
export const DATA_TABLE_ACT_COLUMN_KEY = 'act';
export const DATA_TABLE_ACT_COLUMN_WIDTH_PX = 132;

function parsePercentWeight(width: string | undefined): number | null {
  if (!width?.trim()) return null;
  const raw = width.trim();
  if (!raw.endsWith('%')) return null;
  const pct = parseFloat(raw);
  return Number.isFinite(pct) && pct > 0 ? pct : null;
}

function isFixedDeclarativeWidth(width: string | undefined): boolean {
  if (!width?.trim()) return false;
  const raw = width.trim();
  return raw.endsWith('px') || raw.endsWith('rem');
}

/**
 * Map declarative column widths (% weights) to pixel sizing that fills the scroll host.
 * Action column width is fixed first; rem/px data cols reserved next; % cols share remainder.
 */
export function computeColumnSizingFromSpecs(
  columns: { key: string; width?: string }[],
  tableWidthPx: number,
  hasActionColumn: boolean,
  actionColumnWidthPx: number,
): ColumnSizingState {
  const hostW = Math.max(320, Math.round(tableWidthPx));
  const metaActionW = hasActionColumn ? actionColumnWidthPx : 0;

  const actColumns = columns.filter((c) => c.key === DATA_TABLE_ACT_COLUMN_KEY);
  const dataColumns = columns.filter((c) => c.key !== DATA_TABLE_ACT_COLUMN_KEY);

  const sizing: ColumnSizingState = {};
  let actReserved = 0;
  for (const col of actColumns) {
    const px = parseDataTableColumnWidth(
      col.width,
      hostW,
      DATA_TABLE_ACT_COLUMN_WIDTH_PX,
    );
    sizing[col.key] = px;
    actReserved += px;
  }

  const fixedData = dataColumns.filter((c) => isFixedDeclarativeWidth(c.width));
  const flexData = dataColumns.filter((c) => !isFixedDeclarativeWidth(c.width));

  let fixedReserved = 0;
  for (const col of fixedData) {
    const px = parseDataTableColumnWidth(col.width, hostW, MIN_COLUMN_WIDTH_PX);
    sizing[col.key] = px;
    fixedReserved += px;
  }

  const available = Math.max(
    MIN_COLUMN_WIDTH_PX * Math.max(flexData.length, 1),
    hostW - metaActionW - actReserved - fixedReserved,
  );

  if (flexData.length === 0) {
    return sizing;
  }

  const weights = flexData.map((c) => parsePercentWeight(c.width) ?? 1);
  const weightSum = weights.reduce((a, w) => a + w, 0) || 1;

  let used = 0;
  for (let i = 0; i < flexData.length; i += 1) {
    const col = flexData[i]!;
    const w = weights[i] ?? 1;
    const isLastData = i === flexData.length - 1;
    const px = isLastData
      ? Math.max(MIN_COLUMN_WIDTH_PX, available - used)
      : Math.max(MIN_COLUMN_WIDTH_PX, Math.round((available * w) / weightSum));
    sizing[col.key] = px;
    used += px;
  }

  return sizing;
}

export function colgroupPercentWidth(widthPx: number, totalPx: number): string {
  if (totalPx <= 0) return 'auto';
  const pct = (widthPx / totalPx) * 100;
  return `${pct.toFixed(4)}%`;
}
